import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, ChevronRight, Shield, Users, TrendingUp, Award, CheckCircle, Crown, Sparkles, Globe, FileText, Menu, X, Instagram, Facebook, Youtube, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const lastScrollY = useRef(0);

  // Scroll to a hash anchor when navigating to the home page from another route (e.g. from /contact-us)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      // Wait one frame for layout to settle, then scroll
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
        }
      });
    }
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.fade-in-up, .fade-in-left, .fade-in-right, .scale-in, .fade-in');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Smart header hide/show on scroll + progress indicator
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Calculate scroll progress
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollHeight > 0 ? (currentScrollY / scrollHeight) * 100 : 0;
      setScrollProgress(progress);
      
      // Show back to top button
      setShowBackToTop(currentScrollY > 400);
      
      // Header visibility logic
      if (currentScrollY < 100) {
        // Always show header near top
        setHeaderVisible(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 150) {
        // Scrolling down - hide header
        setHeaderVisible(false);
        setMobileMenuOpen(false); // Close mobile menu when hiding header
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up - show header
        setHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    const duration = 600;
    const start = window.scrollY;
    const startTime = performance.now();

    const easeInOutCubic = (t) => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);
      
      window.scrollTo(0, start * (1 - easeProgress));
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const scrollToSection = (id) => {
    closeMobileMenu();
    const element = document.getElementById(id);
    if (element) {
      const targetPosition = element.offsetTop - 80;
      const startPosition = window.scrollY;
      const distance = targetPosition - startPosition;
      const duration = 600;
      const startTime = performance.now();

      const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
      };

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = easeInOutCubic(progress);
        
        window.scrollTo(0, startPosition + distance * easeProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  };

  const differentiators = [
    {
      icon: Shield,
      title: 'Planning-First Philosophy',
      description: 'We begin with comprehensive planning before discussing products. Your financial strategy drives every recommendation we make.'
    },
    {
      icon: Users,
      title: 'No Account Minimums',
      description: 'Premium accessibility means quality guidance at every wealth level. From Main Street to Wall Street, we serve clients without gatekeeping.'
    },
    {
      icon: TrendingUp,
      title: 'Authentic Cultural Competency',
      description: 'Rooted in lived experience and genuine understanding of diverse wealth-building journeys, particularly within the Melanated diaspora.'
    },
    {
      icon: Award,
      title: 'Fiduciary Standard',
      description: 'Your interests always come first. Full transparency, no conflicted incentives, and institutional experience you can trust.'
    }
  ];

  const stats = [
    { label: 'Launched', value: '2026' },
    { label: 'Fiduciary', value: '100%' },
    { label: 'Account Minimum', value: '$0' }
  ];

  const services = [
    {
      title: 'Financial Organization — Start Here',
      description: "Your financial journey starts here. In one focused session with a fee-only fiduciary wealth advisor, we'll build a complete picture of where you stand — your net worth, cash flow, accounts, and gaps — and hand you a prioritized roadmap for what to do next. No products to sell. No long-term commitment. Just clarity. You'll leave with five tangible deliverables you can act on immediately, plus continued advisor access to keep the momentum going."
    },
    {
      title: 'Insurance Planning',
      description: "Coverage analysis from an advisor with nothing to sell. As a fee-only firm, we don't earn commissions on insurance products — our only agenda is yours. We analyze your current coverage across every insurance type, identify gaps and redundancies, quantify your actual needs, and deliver clear recommendations you can act on with any provider you choose."
    },
    {
      title: 'Financial Planning',
      description: "Your plan is the compass. Everything else follows. We build custom financial strategies across eight planning disciplines — separately designed for individuals and business owners. Every engagement starts with a core set of planning modules, with additional disciplines available based on your needs. You see exactly what you're getting and why — before you sign. Your plan is built in an interactive platform where you can model scenarios, track goals, and see projections in real time."
    },
    {
      title: 'Ongoing Advisory Retainer',
      description: "Your financial life doesn't stop evolving — neither should your plan. Our Ongoing Advisory Retainer keeps your strategy current through quarterly reviews, ongoing tax coordination, life transition guidance, and an annual comprehensive refresh. Retainer scope is matched to the depth of your plan, so you're only paying for the level of advisory you need. The longer your commitment, the more you save."
    },
    {
      title: 'Investment Management',
      description: "Your investments should serve your goals — not the other way around. Our goals-based approach connects every portfolio decision to what matters most in your life. No account minimums. No hidden fees. No commissions. Institutional-grade tools — automated tax-loss harvesting, direct indexing, and professional rebalancing — available to every client. Choose hands-off professional management or stay actively involved at the same fee. As your assets grow, your rate drops and your included services expand."
    },
    {
      title: 'Privé Heritage — Multi-Family Office',
      description: "One team. One strategy. Every detail of your financial life, coordinated. Privé Heritage brings the family office experience — comprehensive planning, investment management, insurance, tax coordination, estate oversight, family governance, and philanthropic strategy — into a single integrated relationship. Without the overhead of building one from scratch. Heritage isn't about the size of your portfolio. It's about the complexity of your life. When your financial picture spans generations, entities, and goals that interconnect in ways no single advisor can manage alone — Heritage is built for that."
    }
  ];

  const testimonials = [
    {
      quote: "Working with Privé has transformed how I think about my financial future. The personalized attention and cultural understanding made all the difference.",
      author: "Client Testimonial"
    },
    {
      quote: "Finally, a wealth advisor who understands my journey and doesn't impose minimums. The planning-first approach built real confidence.",
      author: "Client Testimonial"
    }
  ];

  return (
    <div className="luxury-landing">
      {/* Header */}
      <header className={`navigation-header ${headerVisible ? 'header-visible' : 'header-hidden'}`}>
        <div className="container">
          <div className="nav-content">
            <div className="navigation-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/0u8dfr1l_Prive%CC%81%20WM%20Main%20Logo%202.png" 
                alt="Privé Wealth Management" 
                className="header-logo"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              <ul className="navigation-menu">
                <li>
                  <button onClick={() => scrollToSection('how-we-help')} className="navigation-link">
                    How We Help
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('process')} className="navigation-link">
                    Our Process
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="navigation-link">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('pricing')} className="navigation-link">
                    Pricing
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('heritage')} className="navigation-link">
                    Privé Heritage
                  </button>
                </li>
                <li>
                  <Link to="/contact-us" className="navigation-link" data-testid="home-nav-contact">
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown */}
        <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav-open' : ''}`}>
          <nav>
            <ul className="mobile-menu-list">
              <li>
                <button onClick={() => scrollToSection('how-we-help')} className="mobile-nav-link">
                  How We Help
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('process')} className="mobile-nav-link">
                  Our Process
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="mobile-nav-link">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('pricing')} className="mobile-nav-link">
                  Pricing
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('heritage')} className="mobile-nav-link">
                  Privé Heritage
                </button>
              </li>
              <li>
                <Link to="/contact-us" className="mobile-nav-link" onClick={closeMobileMenu} data-testid="home-mobile-nav-contact">
                  Contact
                </Link>
              </li>
            </ul>
          </nav>
        </div>
        
        {/* Scroll Progress Indicator */}
        <div 
          className="scroll-progress-bar"
          style={{ 
            width: `${scrollProgress}%`,
          }}
        />
      </header>

      {/* Hero Section - Improved */}
      <section className="hero-section-new section-padding">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-content-left fade-in-left">
              <h1 className="hero-large-new">Where Financial Freedom Takes Flight</h1>
              <p className="hero-description">
                Premium wealth management without the gatekeeping. At Privé, your wealth level doesn't determine your access to quality guidance. We serve clients at every stage of their financial journey—from Main Street to Wall Street.
              </p>
              <div className="hero-cta-group">
                <Link to="/contact-us" className="btn-primary" data-testid="hero-contact-link">
                  Schedule an Intro Call
                  <ChevronRight size={16} />
                </Link>
              </div>
            </div>
            <div className="hero-image-right fade-in-right">
              <img 
                src="https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxCbGFjayUyMHByb2Zlc3Npb25hbHN8ZW58MHx8fHwxNzY5MzkzMTgxfDA&ixlib=rb-4.1.0&q=85" 
                alt="Professional Financial Guidance"
                className="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Ticker */}
      <section className="trust-ticker">
        <div className="ticker-content">
          {stats.map((stat, index) => (
            <div key={index} className="ticker-item">
              <span className="ticker-label">{stat.label}</span>
              <span className="ticker-value">{stat.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* How We're Different */}
      <section id="how-we-help" className="differentiators-section section-padding">
        <div className="container">
          <div className="section-header-center fade-in-up">
            <h2 className="hero-medium">Built Around People. Designed for Every Stage.</h2>
            <p className="body-large">
              We don't start with products—we start with you. Here's what sets Privé apart.
            </p>
          </div>
          <div className="differentiators-grid">
            {differentiators.map((item, index) => (
              <div key={index} className="differentiator-card fade-in-up" style={{ transitionDelay: `${index * 0.1}s` }}>
                <div className="diff-icon">
                  <item.icon size={40} strokeWidth={1.5} />
                </div>
                <h3 className="heading-3">{item.title}</h3>
                <p className="body-regular">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="process-section-new section-padding">
        <div className="container">
          <div className="grid-two-column">
            <div className="process-content fade-in-left">
              <h2 className="hero-medium">Our Four-Phase Process</h2>
              <div className="process-steps">
                <div className="process-step-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/d83kjs3w_Prive%CC%81%20WM%20Icon%20no%20background.png" 
                    alt="" 
                    className="process-feather-small"
                  />
                  <div>
                    <h3 className="heading-3">Discovery</h3>
                    <p className="body-regular">We listen deeply to understand your goals, values, and unique financial situation.</p>
                  </div>
                </div>
                <div className="process-step-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/d83kjs3w_Prive%CC%81%20WM%20Icon%20no%20background.png" 
                    alt="" 
                    className="process-feather-small"
                  />
                  <div>
                    <h3 className="heading-3">Design</h3>
                    <p className="body-regular">We craft a tailored strategy built specifically for your needs and aspirations.</p>
                  </div>
                </div>
                <div className="process-step-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/d83kjs3w_Prive%CC%81%20WM%20Icon%20no%20background.png" 
                    alt="" 
                    className="process-feather-small"
                  />
                  <div>
                    <h3 className="heading-3">Delivery</h3>
                    <p className="body-regular">We implement your plan with precision and ongoing guidance every step of the way.</p>
                  </div>
                </div>
                <div className="process-step-item">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/d83kjs3w_Prive%CC%81%20WM%20Icon%20no%20background.png" 
                    alt="" 
                    className="process-feather-small"
                  />
                  <div>
                    <h3 className="heading-3">Diligence</h3>
                    <p className="body-regular">We monitor, adjust, and steward your wealth with long-term care and attention.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="process-image fade-in-right">
              <img 
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxCbGFjayUyMHByb2Zlc3Npb25hbHN8ZW58MHx8fHwxNzY5MzkzMTgxfDA&ixlib=rb-4.1.0&q=85" 
                alt="Collaborative Planning"
                className="section-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Redesigned */}
      <section id="services" className="services-section-new section-padding">
        <div className="container">
          <div className="section-header-center fade-in-up">
            <h2 className="hero-medium">Our Services</h2>
            <p className="body-large">
              Six integrated service pillars—Financial Organization, Insurance Planning, Financial Planning, Ongoing Advisory Retainer, Investment Management, and Privé Heritage—delivered through a fee-only, planning-first process.
            </p>
          </div>
          <div className="services-list">
            {services.map((service, index) => (
              <div key={index} className="service-item-new fade-in-up" style={{ transitionDelay: `${index * 0.15}s` }}>
                <div className="service-number">{String(index + 1).padStart(2, '0')}</div>
                <div className="service-content-new">
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-regular">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" style={{ background: '#fffef2', padding: '120px 0', textAlign: 'center' }}>
        <div className="container">
          <div style={{ marginBottom: '60px' }}>
            <h2 style={{ fontSize: '38px', fontFamily: "'Crimson Text', serif", color: '#333', marginBottom: '24px', textAlign: 'center' }}>
              Transparent, Straightforward Pricing
            </h2>
            <p style={{ fontSize: '18px', color: '#666', textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
              No hidden fees. No account minimums for investment management. Quality guidance at every wealth level.
            </p>
          </div>

          {/* Financial Organizing */}
          <div className="pricing-box-mobile" style={{ background: '#f6f5e8', border: '2px solid #C9A961', padding: '48px', marginBottom: '60px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-2px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '4px', background: '#C9A961' }}></div>
            <h3 style={{ fontSize: '32px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '12px', textAlign: 'center' }}>
              Financial Organizing Services
            </h3>
            <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', maxWidth: '720px', margin: '0 auto 32px', lineHeight: '1.6', fontStyle: 'italic' }}>
              Your financial journey starts here. One focused session with a fee-only fiduciary advisor — clarity on where you stand and a prioritized roadmap for what to do next.
            </p>
            <div className="pricing-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #C9A961' }}>Personal</div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '8px', textAlign: 'center' }}>Personal Session</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>$249</div>
                </div>
                <div style={{ background: 'rgba(201, 169, 97, 0.1)', border: '1px solid #C9A961', padding: '20px', marginTop: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#232e19', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Ongoing Retainer</div>
                  <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.8', textAlign: 'center' }}>
                    Monthly: $99 ($1,188/yr)
                  </div>
                  <div style={{ height: '1px', background: 'rgba(201, 169, 97, 0.4)', margin: '14px auto', maxWidth: '180px' }} />
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Commitment Discounts</div>
                  <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.8', textAlign: 'center' }}>
                    Quarterly: $267 ($1,068/yr — 10% off)
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #C9A961' }}>Business</div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '8px', textAlign: 'center' }}>Business Session</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>$499</div>
                </div>
                <div style={{ background: 'rgba(201, 169, 97, 0.1)', border: '1px solid #C9A961', padding: '20px', marginTop: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#232e19', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Ongoing Retainer</div>
                  <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.8', textAlign: 'center' }}>
                    Monthly: $199 ($2,388/yr)
                  </div>
                  <div style={{ height: '1px', background: 'rgba(201, 169, 97, 0.4)', margin: '14px auto', maxWidth: '180px' }} />
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Commitment Discounts</div>
                  <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.8', textAlign: 'center' }}>
                    Quarterly: $537 ($2,148/yr — 10% off)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Insurance Planning */}
          <div style={{ background: '#f6f5e8', border: '2px solid #C9A961', padding: '48px', marginBottom: '60px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-2px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '4px', background: '#C9A961' }}></div>
            <h3 style={{ fontSize: '32px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '12px', textAlign: 'center' }}>
              Insurance Planning Services
            </h3>
            <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', maxWidth: '720px', margin: '0 auto 32px', lineHeight: '1.6', fontStyle: 'italic' }}>
              Coverage analysis from an advisor with nothing to sell. We identify gaps, quantify your needs, and deliver clear recommendations you can act on with any provider you choose.
            </p>
            <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
              <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                <div style={{ fontSize: '18px', color: '#333', marginBottom: '8px', textAlign: 'center' }}>Standalone Insurance Planning &amp; Needs Analysis</div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>$750</div>
              </div>
              <p style={{ marginTop: '24px', fontSize: '15px', color: '#666', fontStyle: 'italic', textAlign: 'center' }}>
                Bundled at no additional charge in Personal Complete &amp; Comprehensive plans and Business Corporate &amp; Enterprise plans.
              </p>
            </div>
          </div>

          {/* Financial Planning */}
          <div className="pricing-box-mobile" style={{ background: '#f6f5e8', border: '2px solid #C9A961', padding: '48px', marginBottom: '60px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-2px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '4px', background: '#C9A961' }}></div>
            <h3 style={{ fontSize: '32px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '12px', textAlign: 'center' }}>
              Financial Planning
            </h3>
            <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', maxWidth: '760px', margin: '0 auto 32px', lineHeight: '1.6', fontStyle: 'italic' }}>
              Custom strategies across eight planning disciplines — separately designed for individuals and business owners. Choose the depth that matches your situation.
            </p>
            <div className="pricing-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #C9A961' }}>Personal</div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '4px', textAlign: 'center' }}>Core Plan</div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>3 core disciplines</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>Starting at $2,000</div>
                </div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '4px', textAlign: 'center' }}>Complete Plan</div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>5 disciplines • Insurance included</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>Starting at $4,500</div>
                </div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '4px', textAlign: 'center' }}>Comprehensive Plan</div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>All 8 disciplines</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>Starting at $12,500</div>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #C9A961' }}>Business</div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '4px', textAlign: 'center' }}>Foundational Plan</div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>3 core disciplines</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>Starting at $3,500</div>
                </div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '4px', textAlign: 'center' }}>Corporate Plan</div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>5 disciplines • Insurance included</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>Starting at $6,500</div>
                </div>
                <div style={{ background: '#fffef2', padding: '24px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '18px', color: '#333', marginBottom: '4px', textAlign: 'center' }}>Enterprise Plan</div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>All 8 disciplines</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#232e19', fontFamily: "'Crimson Text', serif", textAlign: 'center' }}>Starting at $15,000</div>
                </div>
              </div>
            </div>

            {/* Centered Ongoing Retainer block — applies to all FP tiers */}
            <div style={{ marginTop: '48px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
              <div style={{ background: 'rgba(201, 169, 97, 0.1)', border: '1px solid #C9A961', padding: '32px' }}>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#232e19', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px', textAlign: 'center' }}>Ongoing Retainer</div>
                <p style={{ fontSize: '13px', color: '#666', textAlign: 'center', marginBottom: '24px', fontStyle: 'italic' }}>
                  Retainer scope matches your plan tier.
                </p>
                <div className="pricing-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Personal Retainer</div>
                    <div style={{ fontSize: '14px', color: '#333', lineHeight: '2' }}>
                      <strong>Core:</strong> $249/mo<br/>
                      <strong>Complete:</strong> $399/mo<br/>
                      <strong>Comprehensive:</strong> $549/mo
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '14px' }}>Business Retainer</div>
                    <div style={{ fontSize: '14px', color: '#333', lineHeight: '2' }}>
                      <strong>Foundational:</strong> $399/mo<br/>
                      <strong>Corporate:</strong> $599/mo<br/>
                      <strong>Enterprise:</strong> $799/mo
                    </div>
                  </div>
                </div>

                {/* Visual divider */}
                <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #C9A961, transparent)', margin: '32px auto 24px', maxWidth: '500px' }} />

                {/* Commitment Discounts subsection */}
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#232e19', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px', textAlign: 'center' }}>Commitment Discounts</div>
                <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginBottom: '20px', fontStyle: 'italic' }}>
                  The longer your commitment, the more you save.
                </p>
                <div className="pricing-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: '#333', lineHeight: '2' }}>
                      <strong>Core:</strong> $672/qtr&nbsp;·&nbsp;$2,540/yr<br/>
                      <strong>Complete:</strong> $1,077/qtr&nbsp;·&nbsp;$4,070/yr<br/>
                      <strong>Comprehensive:</strong> $1,482/qtr&nbsp;·&nbsp;$5,600/yr
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '13px', color: '#333', lineHeight: '2' }}>
                      <strong>Foundational:</strong> $1,077/qtr&nbsp;·&nbsp;$4,070/yr<br/>
                      <strong>Corporate:</strong> $1,617/qtr&nbsp;·&nbsp;$6,110/yr<br/>
                      <strong>Enterprise:</strong> $2,157/qtr&nbsp;·&nbsp;$8,150/yr
                    </div>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '20px', fontStyle: 'italic' }}>
                  Quarterly billing 10% off · Annual billing 15% off
                </p>
              </div>
            </div>
          </div>

          {/* Investment Management */}
          <div className="pricing-box-mobile" style={{ background: '#f6f5e8', border: '2px solid #C9A961', padding: '48px', marginBottom: '60px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-2px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '4px', background: '#C9A961' }}></div>
            <h3 style={{ fontSize: '32px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '12px', textAlign: 'center' }}>
              Investment Management Fees
            </h3>
            <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', maxWidth: '780px', margin: '0 auto 32px', lineHeight: '1.6', fontStyle: 'italic' }}>
              No account minimums. No hidden fees. No commissions. As your assets grow, your rate drops.
            </p>
            <div className="pricing-fee-table" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px', background: '#C9A961', border: '2px solid #C9A961', marginBottom: '32px' }}>
              <div style={{ background: '#232e19', color: '#fff', padding: '20px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Personal Account Threshold</div>
              <div style={{ background: '#232e19', color: '#fff', padding: '20px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Business Account Threshold</div>
              <div style={{ background: '#232e19', color: '#fff', padding: '20px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Annual Fee Rate</div>
              
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.6' }}>Under $1,000,000</div>
              </div>
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.6' }}>Under $3,000,000</div>
              </div>
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '600', color: '#C9A961', fontFamily: "'Crimson Text', serif" }}>1.00%</div>
              </div>

              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.6' }}>$1,000,000 - $4,999,999</div>
              </div>
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.6' }}>$3,000,000 - $9,999,999</div>
              </div>
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '600', color: '#C9A961', fontFamily: "'Crimson Text', serif" }}>0.75%</div>
              </div>

              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.6' }}>$5,000,000 and above</div>
              </div>
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '15px', color: '#333', lineHeight: '1.6' }}>$10,000,000 and above</div>
              </div>
              <div style={{ background: '#fffef2', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: '600', color: '#C9A961', fontFamily: "'Crimson Text', serif" }}>0.50%</div>
              </div>
            </div>
            <div style={{ marginTop: '32px', padding: '24px', background: 'rgba(201, 169, 97, 0.1)', borderLeft: '3px solid #C9A961', borderRight: '3px solid #C9A961', fontSize: '14px', color: '#666', lineHeight: '1.6', textAlign: 'center' }}>
              <em>All fees are negotiable based on complexity, scope, and relationship size.</em>
            </div>
          </div>

          {/* CTA */}
          <div style={{ maxWidth: '700px', margin: '80px auto 0', textAlign: 'center' }} className="fade-in-up">
            <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#666', marginBottom: '32px', textAlign: 'center' }}>
              Interested in learning more about our services to determine which is the right fit for your financial goals? Schedule an introductory call with our team—no obligation, no pressure.
            </p>
            <Link to="/contact-us" className="btn-primary" data-testid="pricing-contact-link">
              Schedule Your Intro Call
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Privé Heritage Section */}
      <section id="heritage" style={{ background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)', border: '4px solid #C9A961', padding: '120px 0', textAlign: 'center', position: 'relative' }}>
        <div className="container">
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div className="fade-in-up" style={{ marginBottom: '32px', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(201, 169, 97, 0.2)', border: '2px solid #C9A961', padding: '10px 24px', borderRadius: '30px', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1.5px', color: '#C9A961', fontWeight: '600' }}>
                <Crown size={16} />
                <span>Multi-Family Office</span>
              </div>
            </div>
            
            {/* Privé Heritage Logo */}
            <div className="fade-in-up" style={{ marginBottom: '40px', textAlign: 'center' }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_bf35d6fd-b98b-4ef5-9f11-3f4816323cfa/artifacts/076zpvls_Prive%20Heritage%20Logo.jpg" 
                alt="Privé Heritage" 
                style={{ maxWidth: '280px', height: 'auto', display: 'inline-block' }}
              />
            </div>
            
            <p style={{ fontSize: '20px', lineHeight: '1.7', color: 'rgba(201, 169, 97, 0.95)', marginBottom: '60px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' }}>
              Our premier integrated wealth management offering, structured as a Multi-Family Office (MFO) framework. A single point of contact coordinating your entire financial life—encompassing not just investments, but the full ecosystem of advisors, entities, generations, and goals.
            </p>

            <div className="heritage-grid-3col" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px', marginBottom: '60px' }}>
              <div className="fade-in-up" style={{ background: 'rgba(201, 169, 97, 0.1)', backdropFilter: 'blur(10px)', padding: '40px 32px', border: '2px solid #C9A961', textAlign: 'center', transition: 'all 0.3s ease' }}>
                <div style={{ color: '#C9A961', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                  <Shield size={32} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '12px', textAlign: 'center' }}>
                  Investment Management
                </h3>
                <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#C9A961', opacity: '0.85', textAlign: 'center' }}>
                  Strategic asset allocation, manager selection, private investments, and comprehensive portfolio reporting included.
                </p>
              </div>

              <div className="fade-in-up" style={{ background: 'rgba(201, 169, 97, 0.1)', backdropFilter: 'blur(10px)', padding: '40px 32px', border: '2px solid #C9A961', textAlign: 'center', transition: 'all 0.3s ease' }}>
                <div style={{ color: '#C9A961', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                  <FileText size={32} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '12px', textAlign: 'center' }}>
                  Tax & Estate Planning
                </h3>
                <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#C9A961', opacity: '0.85', textAlign: 'center' }}>
                  Multi-jurisdiction coordination, estate structures, intergenerational wealth transfer, and legacy planning.
                </p>
              </div>

              <div className="fade-in-up" style={{ background: 'rgba(201, 169, 97, 0.1)', backdropFilter: 'blur(10px)', padding: '40px 32px', border: '2px solid #C9A961', textAlign: 'center', transition: 'all 0.3s ease' }}>
                <div style={{ color: '#C9A961', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                  <Users size={32} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '12px', textAlign: 'center' }}>
                  Family Governance
                </h3>
                <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#C9A961', opacity: '0.85', textAlign: 'center' }}>
                  Family mission, next-gen education, family meetings, and structured decision-making frameworks.
                </p>
              </div>

              <div className="fade-in-up" style={{ background: 'rgba(201, 169, 97, 0.1)', backdropFilter: 'blur(10px)', padding: '40px 32px', border: '2px solid #C9A961', textAlign: 'center', transition: 'all 0.3s ease' }}>
                <div style={{ color: '#C9A961', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                  <Sparkles size={32} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '12px', textAlign: 'center' }}>
                  Philanthropy & Impact
                </h3>
                <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#C9A961', opacity: '0.85', textAlign: 'center' }}>
                  Charitable foundations, donor-advised funds, impact investing, and legacy giving strategy.
                </p>
              </div>

              <div className="fade-in-up" style={{ background: 'rgba(201, 169, 97, 0.1)', backdropFilter: 'blur(10px)', padding: '40px 32px', border: '2px solid #C9A961', textAlign: 'center', transition: 'all 0.3s ease' }}>
                <div style={{ color: '#C9A961', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                  <Globe size={32} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '12px', textAlign: 'center' }}>
                  Advisor Coordination
                </h3>
                <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#C9A961', opacity: '0.85', textAlign: 'center' }}>
                  Seamless collaboration with your external advisors—CPAs, attorneys, and specialists—all coordinated.
                </p>
              </div>

              <div className="fade-in-up" style={{ background: 'rgba(201, 169, 97, 0.1)', backdropFilter: 'blur(10px)', padding: '40px 32px', border: '2px solid #C9A961', textAlign: 'center', transition: 'all 0.3s ease' }}>
                <div style={{ color: '#C9A961', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                  <Crown size={32} strokeWidth={1.5} />
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '400', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '12px', textAlign: 'center' }}>
                  White-Glove Service
                </h3>
                <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#C9A961', opacity: '0.85', textAlign: 'center' }}>
                  Priority access, dedicated attention, and depth of partnership beyond standard advisory services.
                </p>
              </div>
            </div>

            {/* Fee Structure */}
            <div className="fade-in-up" style={{ marginTop: '60px', padding: '50px 40px', background: 'rgba(201, 169, 97, 0.12)', border: '2px solid #C9A961', borderRadius: '0px' }}>
              <p style={{ fontSize: '18px', fontWeight: '600', color: '#C9A961', marginBottom: '40px', textTransform: 'uppercase', letterSpacing: '1.5px', textAlign: 'center' }}>
                Heritage Fee Structure
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px', maxWidth: '1100px', margin: '0 auto' }}>
                {/* Heritage Family */}
                <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', border: '2px solid rgba(201, 169, 97, 0.5)', textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', color: '#C9A961', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Heritage Family</p>
                  <p style={{ fontSize: '36px', fontWeight: '300', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '8px' }}>$30,000</p>
                  <p style={{ fontSize: '13px', color: 'rgba(201, 169, 97, 0.7)', marginBottom: '20px' }}>annually • 1.00% effective on cap</p>
                  <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #C9A961, transparent)', marginBottom: '20px' }} />
                  <p style={{ fontSize: '13px', color: 'rgba(201, 169, 97, 0.85)', lineHeight: '1.7' }}>
                    For $3M+ household net worth<br/>
                    Includes up to $3M AUM<br/>
                    Single-generation focus
                  </p>
                </div>

                {/* Heritage Multi-Gen */}
                <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', border: '2px solid #C9A961', textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', color: '#C9A961', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Heritage Multi-Gen</p>
                  <p style={{ fontSize: '36px', fontWeight: '300', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '8px' }}>$60,000</p>
                  <p style={{ fontSize: '13px', color: 'rgba(201, 169, 97, 0.7)', marginBottom: '20px' }}>annually • 0.80% effective on cap</p>
                  <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #C9A961, transparent)', marginBottom: '20px' }} />
                  <p style={{ fontSize: '13px', color: 'rgba(201, 169, 97, 0.85)', lineHeight: '1.7' }}>
                    For $7.5M+ household net worth<br/>
                    Includes up to $7.5M AUM<br/>
                    Multi-generational planning
                  </p>
                </div>

                {/* Heritage Legacy */}
                <div style={{ padding: '30px', background: 'rgba(0, 0, 0, 0.3)', border: '2px solid rgba(201, 169, 97, 0.5)', textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', color: '#C9A961', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Heritage Legacy</p>
                  <p style={{ fontSize: '36px', fontWeight: '300', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '8px' }}>$100,000+</p>
                  <p style={{ fontSize: '13px', color: 'rgba(201, 169, 97, 0.7)', marginBottom: '20px' }}>annually • 0.67% effective on cap</p>
                  <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #C9A961, transparent)', marginBottom: '20px' }} />
                  <p style={{ fontSize: '13px', color: 'rgba(201, 169, 97, 0.85)', lineHeight: '1.7' }}>
                    For $15M+ household net worth<br/>
                    Includes up to $15M AUM<br/>
                    Full family office experience
                  </p>
                </div>
              </div>

              <p style={{ fontSize: '14px', color: 'rgba(201, 169, 97, 0.75)', marginTop: '30px', textAlign: 'center', lineHeight: '1.7' }}>
                Assets managed beyond included AUM cap: 0.35% annually • 15% discount for annual billing • One-year minimum commitment
              </p>
            </div>

            {/* Founding Heritage Program - Special Highlight Box */}
            <div className="fade-in-up" style={{ marginTop: '40px', padding: '50px 50px', background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.25) 0%, rgba(201, 169, 97, 0.15) 100%)', border: '3px solid #C9A961', borderRadius: '0px', position: 'relative', overflow: 'hidden' }}>
              {/* Crown icon decoration */}
              <div style={{ position: 'absolute', top: '-30px', right: '-30px', opacity: '0.1' }}>
                <Crown size={180} strokeWidth={1} style={{ color: '#C9A961' }} />
              </div>
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
                  <Crown size={24} style={{ color: '#C9A961' }} />
                  <p style={{ fontSize: '22px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '2px' }}>
                    Founding Heritage Program
                  </p>
                  <Crown size={24} style={{ color: '#C9A961' }} />
                </div>
                
                <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#C9A961', marginBottom: '32px', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' }}>
                  Limited opportunity for early-adopter clients to establish Heritage with exclusive benefits during our growth phase.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '40px', maxWidth: '700px', margin: '0 auto 32px auto' }}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '48px', fontWeight: '600', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '8px' }}>15%</p>
                    <p style={{ fontSize: '15px', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Founding Discount</p>
                    <p style={{ fontSize: '14px', color: 'rgba(201, 169, 97, 0.8)', marginTop: '8px', lineHeight: '1.6' }}>On annual Heritage fee</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '48px', fontWeight: '600', fontFamily: "'Crimson Text', serif", color: '#C9A961', marginBottom: '8px' }}>3-Year</p>
                    <p style={{ fontSize: '15px', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>Rate Lock</p>
                    <p style={{ fontSize: '14px', color: 'rgba(201, 169, 97, 0.8)', marginTop: '8px', lineHeight: '1.6' }}>Discounted rate guaranteed</p>
                  </div>
                </div>

                <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #C9A961, transparent)', margin: '32px auto', maxWidth: '600px' }} />

                <p style={{ fontSize: '15px', lineHeight: '1.7', color: 'rgba(201, 169, 97, 0.85)', marginBottom: '28px', maxWidth: '750px', marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' }}>
                  <strong style={{ color: '#C9A961' }}>Limited to 10 founding clients.</strong> Stack with annual billing discount for up to 27.75% total savings. Full details and eligibility discussed during consultation.
                </p>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Link to="/contact-us" className="btn-primary-white" data-testid="founding-program-contact-link" style={{ background: '#C9A961', color: '#000', border: 'none', fontSize: '13px', padding: '14px 32px' }}>
                    Learn More About Founding Program
                    <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="fade-in-up" style={{ marginTop: '40px', padding: '35px', background: 'rgba(201, 169, 97, 0.12)', border: '2px solid rgba(201, 169, 97, 0.5)', borderRadius: '0px', textAlign: 'center' }}>
              <p style={{ fontSize: '16px', fontWeight: '600', color: '#C9A961', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                Net Worth-Based Eligibility
              </p>
              <p style={{ fontSize: '15px', lineHeight: '1.7', color: 'rgba(201, 169, 97, 0.9)', maxWidth: '800px', margin: '0 auto' }}>
                Heritage acceptance is based on household complexity and strategic planning needs, measured by total net worth—not solely liquid investments. Eligibility begins at $3M total household net worth.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="fade-in-up" style={{ marginTop: '48px', display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact-us" className="btn-primary-white" data-testid="heritage-consultation-contact-link">
                Schedule Heritage Consultation
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Privé Section */}
      <section className="why-section section-padding-small">
        <div className="container">
          <div className="grid-two-column">
            <div className="why-image fade-in-left">
              <img 
                src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxCbGFjayUyMHByb2Zlc3Npb25hbHN8ZW58MHx8fHwxNzY5MzkzMTgxfDA&ixlib=rb-4.1.0&q=85" 
                alt="Privé Team"
                className="section-image"
              />
            </div>
            <div className="why-content fade-in-right">
              <h2 className="heading-1">Why We Built Privé</h2>
              <p className="body-large">
                We started Privé Wealth Management because everyone deserves access to premium financial guidance. With institutional experience and a fiduciary-first approach, we deliver advice grounded in cultural intelligence and genuine partnership.
              </p>
              <p className="body-large">
                Whether you're just beginning your wealth-building journey or managing significant assets, you receive the same level of attention, expertise, and care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Hidden for compliance. Uncomment when approved.
      <section className="testimonial-section section-padding-small">
        <div className="container">
          <div className="testimonial-container fade-in-up">
            <div className="testimonial-content">
              <p className="testimonial-quote">"{testimonials[currentTestimonial].quote}"</p>
              <p className="testimonial-author">— {testimonials[currentTestimonial].author}</p>
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Final CTA */}
      <section id="contact" className="cta-section-new section-padding">
        <div className="container">
          <div className="cta-content-new fade-in-up">
            <h2 className="hero-medium-white">Let's Take the First Step Together</h2>
            <p className="body-large-white">
              No obligation, no pressure—just a conversation about your financial future.
            </p>
            <Link to="/contact-us" className="btn-primary-white" data-testid="final-cta-contact-link">
              Schedule an Intro Call
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo fade-in">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/0u8dfr1l_Prive%CC%81%20WM%20Main%20Logo%202.png" 
                alt="Privé Wealth Management" 
                className="footer-logo-img"
              />
            </div>
            
            {/* Social Media Icons */}
            <div className="footer-social fade-in">
              <a 
                href="https://www.instagram.com/officialprivewm/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.facebook.com/privewm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.youtube.com/@OfficialPriveWM" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="YouTube"
              >
                <Youtube size={20} />
              </a>
              <a 
                href="https://www.tiktok.com/@officialprivewm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="TikTok"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/company/officialprivewm" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://x.com/OfficialPriveWM" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="X (Twitter)"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>

            <div className="footer-links fade-in-up">
              <a 
                href="https://customer-assets.emergentagent.com/job_215f2b22-f0a3-4699-9cdc-907a59cf6351/artifacts/0it15obz_Prive-Form-CRS.pdf" 
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Form CRS
              </a>
              <a 
                href="https://files.adviserinfo.sec.gov/IAPD/Content/Common/crd_iapd_Brochure.aspx?BRCHR_VRSN_ID=1043573" 
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                ADV Firm Brochure
              </a>
              <a 
                href="https://adviserinfo.sec.gov" 
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                SEC Investment Adviser Search
              </a>
              <Link 
                to="/terms" 
                className="footer-link"
              >
                Terms of Service
              </Link>
              <Link 
                to="/privacy" 
                className="footer-link"
              >
                Privacy Policy
              </Link>
            </div>
            <div className="footer-disclaimer fade-in-up">
              <p className="body-small">
                Privé Wealth Management, LLC is a Georgia-registered investment advisory firm. Registration does not imply a certain level of skill or training. All investment advisory services are provided in a fiduciary capacity and are tailored to the individual needs and objectives of each client. Advisory services are only offered to clients or prospective clients where Privé Wealth Management, LLC and its representatives are properly registered or exempt from registration. Past performance is not indicative of future results. No guarantees are made regarding investment outcomes.
              </p>
            </div>
            <div className="footer-bottom">
              <p className="body-small">© 2026 Privé Wealth Management, LLC. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button 
          className="back-to-top" 
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

export default Home;
